// client/src/components/pages/DriverPWA/index.tsx (converted from .js, ST-00216)
//
// ST-00228 (audit TRK-20): zones that are not polygons are circles whatever their type
// says (the seeded zones say 'CIRCLE'; the page only knew 'CIRCULAR' and never found the
// phone inside them), a zone response that is not a list loads no zones, and an account
// that may not send positions (GET /geofences 403, or `location:error` 'forbidden') is
// told so while its fixes are kept in the offline queue instead of being sent and lost.
import { useState, useEffect, useRef, useCallback } from 'react';
import type { ComponentType, FormEvent } from 'react';
import type { Socket } from 'socket.io-client';
import LocationTrackerJs from './LocationTracker';
import DriverDashboardJs from './DriverDashboard';
import OfflineSync from './OfflineSync';
import type { OfflineSyncHandle } from './OfflineSync';
import api from '../../../services/api';
import { connectSocket as connectSocketJs } from '../../../services/socketClient';
import { FaTruck, FaWifi, FaExclamationTriangle, FaMapMarkerAlt } from 'react-icons/fa';
import type { ConnectSocketFn, LatLngPair } from '../../../types/clientPages/geofencing';
import type {
  DriverGeofence,
  DriverGeofencesBody,
  DriverLocationMessage,
  LocationErrorMessage,
  LocationFix,
} from '../../../types/clientPages/driverPWA';

/** A zone visit, logged when the phone leaves the zone. */
interface ShiftLogEntry {
  zoneName: string;
  enteredAt: Date;
  exitedAt: Date;
  durationMin: number;
}

// Typed views of the JavaScript modules this page uses.
const connectSocket = connectSocketJs as unknown as ConnectSocketFn;
const LocationTracker = LocationTrackerJs as unknown as ComponentType<{
  active: boolean;
  onLocationUpdate: (position: LocationFix) => void;
}>;
const DriverDashboard = DriverDashboardJs as unknown as ComponentType<{
  position: LocationFix | null;
  tracking: boolean;
  connected: boolean;
  currentZone: string | null;
  zoneEntryTime: Date | null;
  shiftLog?: ShiftLogEntry[];
}>;
/** An axios-style failure as this page reads it. */
type RequestFailure = {
  response?: { status?: number; data?: { message?: string } };
  message?: string;
};

/**
 * Ray-casting point-in-polygon test.
 * polygon is an array of [lat, lng] pairs.
 */
function isPointInPolygon(lat: number, lng: number, polygon: LatLngPair[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i]![0],
      yi = polygon[i]![1];
    const xj = polygon[j]![0],
      yj = polygon[j]![1];
    const intersect = yi > lng !== yj > lng && lat < ((xj - xi) * (lng - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Check if point is inside a circular geofence.
 * Uses the Haversine formula for distance.
 */
function isPointInCircle(
  lat: number,
  lng: number,
  centerLat: number,
  centerLng: number,
  radiusMeters: number
): boolean {
  const R = 6371000; // Earth radius in meters
  const dLat = ((lat - centerLat) * Math.PI) / 180;
  const dLng = ((lng - centerLng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((centerLat * Math.PI) / 180) *
      Math.cos((lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return distance <= radiusMeters;
}

/** A zone's polygon ring, from JSON text or an already-parsed array; null if unusable. */
function polygonOf(gf: DriverGeofence): LatLngPair[] | null {
  if (!gf.Coordinates) return null;
  try {
    const coords: unknown =
      typeof gf.Coordinates === 'string' ? JSON.parse(gf.Coordinates) : gf.Coordinates;
    return Array.isArray(coords) && coords.length >= 3 ? (coords as LatLngPair[]) : null;
  } catch (e) {
    console.warn('Failed to parse geofence coordinates for', gf.Name, e);
    return null;
  }
}

/**
 * Determine which geofence (if any) the point falls inside. Returns the geofence
 * Name or null. Same rule as the server (geofenceService.isInsideGeofence): a POLYGON
 * with a usable ring is tested as a polygon; every other zone is a circle around its
 * centre ('CIRCLE' in the seeded zones, 'CIRCULAR' from the zone form).
 */
export function detectZone(lat: number, lng: number, geofences: DriverGeofence[]): string | null {
  for (const gf of geofences) {
    const polygon = gf.GeofenceType === 'POLYGON' ? polygonOf(gf) : null;
    if (polygon) {
      if (isPointInPolygon(lat, lng, polygon)) return gf.Name;
    } else if (
      gf.Latitude != null &&
      gf.Longitude != null &&
      gf.RadiusMeters != null &&
      isPointInCircle(lat, lng, gf.Latitude, gf.Longitude, gf.RadiusMeters)
    ) {
      return gf.Name;
    }
  }
  return null;
}

/** GET /geofences body -> the zone list; anything that is not a list is no zones. */
function zonesFrom(body: DriverGeofencesBody | undefined): DriverGeofence[] {
  if (Array.isArray(body)) return body;
  const data = (body as { data?: unknown } | undefined)?.data;
  return Array.isArray(data) ? (data as DriverGeofence[]) : [];
}

// Shown when the server refuses this device's session (ST-00214). Positions are
// only accepted from a signed-in session, under that session's organisation; a
// dedicated driver/device login is a follow-up.
const SIGN_IN_REQUIRED_MESSAGE =
  'Sign-in required: live sending is off on this device. Sign in to LexiBridge on this phone, then reopen this page. Positions are kept on the phone until then.';
const NO_ORGANISATION_MESSAGE =
  'This LexiBridge account has no active organisation: live sending is off. Positions are kept on the phone.';
// The session is fine but the account lacks "Send Vehicle Positions"
// (geofencing.positions.write, ST-00228 / audit TRK-10).
const POSITIONS_FORBIDDEN_MESSAGE =
  'This account may not send positions: it needs the "Send Vehicle Positions" permission. Ask your LexiBridge administrator. Positions are kept on the phone.';

/** A 403 from the API: no organisation, or no permission to send positions. */
const forbiddenMessage = (err: RequestFailure): string =>
  /organi[sz]ation/i.test(err.response?.data?.message || '')
    ? NO_ORGANISATION_MESSAGE
    : POSITIONS_FORBIDDEN_MESSAGE;

export default function DriverPWA() {
  // The server takes the organisation from the session cookie (it used to trust
  // a tenantId stored here, default 1, for every phone). AuthContext does not
  // check the session on /driver (it forces authenticated=false there), so the
  // page simply tries: the geofence GET and the socket go out with the cookie,
  // and only a refusal from the server shows the sign-in alert. Without a
  // session the page stays usable offline but sends nothing.
  const [connected, setConnected] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [tracking, setTracking] = useState(false);
  const [currentPosition, setCurrentPosition] = useState<LocationFix | null>(null);
  const [regNo, setRegNo] = useState(() => localStorage.getItem('driver_regNo') || '');
  const [isSetup, setIsSetup] = useState(() => !!localStorage.getItem('driver_regNo'));
  const [positionCount, setPositionCount] = useState(0);
  // The latest count for the location callback's sampled zone log (state would be stale there).
  const positionCountRef = useRef(0);
  const [offlineCount, setOfflineCount] = useState(0);
  const socketRef = useRef<Socket | null>(null);
  const offlineSyncRef = useRef<OfflineSyncHandle>(null);
  // The server refused this account's positions (403 / 'forbidden'): queue, don't send.
  const positionsRefusedRef = useRef(false);

  // Geofence & zone tracking state
  const [geofences, setGeofences] = useState<DriverGeofence[]>([]);
  const [currentZone, setCurrentZone] = useState<string | null>(null);
  const [zoneEntryTime, setZoneEntryTime] = useState<Date | null>(null);
  const [shiftLog, setShiftLog] = useState<ShiftLogEntry[]>([]);

  // Wake Lock ref
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  // Refs to hold latest zone values for the location callback
  const currentZoneRef = useRef<string | null>(null);
  const zoneEntryTimeRef = useRef<Date | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    currentZoneRef.current = currentZone;
  }, [currentZone]);
  useEffect(() => {
    zoneEntryTimeRef.current = zoneEntryTime;
  }, [zoneEntryTime]);

  // Geofence loading state for diagnostics
  const [geofenceError, setGeofenceError] = useState<string | null>(null);

  // Fetch geofences. Same API base as the rest of the app (the old
  // `${hostname}:5002` URL is unreachable behind the proxy). An expired access
  // cookie is refreshed by the shared API client; a 401 that survives the
  // refresh means there is no session on this device.
  useEffect(() => {
    let cancelled = false;
    async function fetchGeofences() {
      try {
        setGeofenceError(null);
        const res = await api.get<DriverGeofencesBody | undefined>('/geofences');
        const data = zonesFrom(res.data);
        console.log(
          `[PWA] Geofences loaded: ${data.length}`,
          data.map((g) => `${g.Name} (${g.GeofenceType}, coords: ${g.Coordinates ? 'yes' : 'NO'})`)
        );
        if (!cancelled) {
          setGeofences(data);
          if (data.length === 0) {
            setGeofenceError('No geofences returned — check tenant context');
          }
        }
      } catch (caught) {
        const err = caught as RequestFailure;
        console.error('[PWA] Failed to fetch geofences:', err);
        if (cancelled) return;
        if (err.response?.status === 401) {
          setSessionError(SIGN_IN_REQUIRED_MESSAGE);
        } else if (err.response?.status === 403) {
          positionsRefusedRef.current = true;
          setSessionError(forbiddenMessage(err));
        } else {
          setGeofenceError(`Failed to load zones: ${err.response?.status || err.message}`);
        }
      }
    }
    fetchGeofences();
    return () => {
      cancelled = true;
    };
  }, []);

  // Wake Lock: acquire when tracking starts, release when it stops
  useEffect(() => {
    async function requestWakeLock() {
      if ('wakeLock' in navigator && tracking) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          wakeLockRef.current.addEventListener('release', () => {
            console.log('Wake lock released');
          });
          console.log('Wake lock acquired');
        } catch (err) {
          console.warn('Wake lock request failed:', err);
        }
      }
    }

    if (tracking) {
      requestWakeLock();
    } else {
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    }

    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, [tracking]);

  // Re-acquire wake lock when page becomes visible again (browser may release it)
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible' && tracking && !wakeLockRef.current) {
        navigator.wakeLock
          ?.request('screen')
          .then((lock) => {
            wakeLockRef.current = lock;
          })
          .catch(() => {});
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [tracking]);

  // Socket.IO connection — same origin as the API, authenticated by the session
  // cookie; the server joins this session's own tenant room. A refused session
  // (after one refresh attempt in connectSocket) shows the alert.
  useEffect(() => {
    if (!isSetup) return undefined;

    const socket = connectSocket('/tracking', {
      reconnection: true,
      reconnectionDelay: 2000,
      onAuthFailed: (err?: Error) =>
        setSessionError(
          err?.message === 'forbidden' ? NO_ORGANISATION_MESSAGE : SIGN_IN_REQUIRED_MESSAGE
        ),
    });

    socket.on('connect', () => {
      setConnected(true);
      // A refusal of this account's positions outlives a reconnect (it is not a session problem).
      if (!positionsRefusedRef.current) setSessionError(null);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('location:error', (payload?: LocationErrorMessage) => {
      console.warn('[PWA] position rejected by the server:', payload?.error);
      if (payload?.error === 'forbidden') {
        positionsRefusedRef.current = true;
        setSessionError(POSITIONS_FORBIDDEN_MESSAGE);
      }
    });

    socketRef.current = socket;
    return () => {
      socket.disconnect();
    };
  }, [isSetup]);

  // Handle location updates from tracker
  const handleLocationUpdate = useCallback(
    async (position: LocationFix) => {
      setCurrentPosition(position);
      positionCountRef.current += 1;
      setPositionCount(positionCountRef.current);

      // --- Zone detection ---
      if (geofences.length > 0) {
        const detectedZone = detectZone(position.latitude, position.longitude, geofences);
        if (positionCountRef.current % 10 === 1) {
          console.log(
            `[PWA Zone] pos=(${position.latitude.toFixed(6)}, ${position.longitude.toFixed(6)}), zones=${geofences.length}, detected=${detectedZone || 'NONE'}`
          );
        }
        const prevZone = currentZoneRef.current;
        const prevEntryTime = zoneEntryTimeRef.current;

        if (detectedZone !== prevZone) {
          // Exiting previous zone — log it
          if (prevZone && prevEntryTime) {
            const exitedAt = new Date();
            const durationMin =
              Math.round(((exitedAt.getTime() - prevEntryTime.getTime()) / 60000) * 10) / 10;
            setShiftLog((prev) => [
              ...prev,
              {
                zoneName: prevZone,
                enteredAt: prevEntryTime,
                exitedAt,
                durationMin,
              },
            ]);
          }

          // Entering new zone (or leaving all zones)
          if (detectedZone) {
            setZoneEntryTime(new Date());
          } else {
            setZoneEntryTime(null);
          }
          setCurrentZone(detectedZone);

          // Haptic feedback on zone change
          if (navigator.vibrate) {
            navigator.vibrate([200, 100, 200]);
          }
        }
      }

      const locationData: DriverLocationMessage = {
        regNo,
        lat: position.latitude,
        lng: position.longitude,
        speed: position.speed,
        heading: position.heading,
        accuracy: position.accuracy,
        altitude: position.altitude,
        timestamp: new Date().toISOString(),
      };

      if (socketRef.current?.connected && !positionsRefusedRef.current) {
        socketRef.current.emit('location:update', locationData);
        // Sync any offline queue
        if (offlineSyncRef.current) {
          const queued = await offlineSyncRef.current.getQueuedCount();
          if (queued > 0) {
            offlineSyncRef.current.syncQueue();
          }
        }
      } else {
        // Queue offline
        if (offlineSyncRef.current) {
          await offlineSyncRef.current.queueLocation(locationData);
          setOfflineCount((prev) => prev + 1);
        }
      }
    },
    [regNo, geofences]
  );

  const handleSetup = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!regNo.trim()) return;
    localStorage.setItem('driver_regNo', regNo.trim());
    localStorage.removeItem('driver_tenantId'); // no longer used: tenant comes from the session
    setIsSetup(true);
  };

  const handleReset = () => {
    localStorage.removeItem('driver_regNo');
    localStorage.removeItem('driver_tenantId');
    setIsSetup(false);
    setTracking(false);
    setRegNo('');
  };

  // Setup screen
  if (!isSetup) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-surface rounded-2xl shadow-lg p-8 max-w-sm w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaTruck className="text-teal-600" size={28} />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Loader Operator</h1>
            <p className="text-sm text-gray-500 mt-1">
              Enter your loader ID to start stockpile tracking
            </p>
          </div>

          <form onSubmit={handleSetup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Loader ID</label>
              <input
                type="text"
                value={regNo}
                onChange={(e) => setRegNo(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-center text-lg font-bold tracking-wider focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="ABC 123 GP"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full py-3 bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 transition-colors"
            >
              Start
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Status bar */}
      <div
        className={`px-4 py-2 text-xs font-medium text-center ${connected ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}
      >
        <FaWifi className="inline mr-1" size={10} />
        {connected ? 'Connected' : 'Offline'}
        {offlineCount > 0 && !connected && ` — ${offlineCount} queued`}
        {' — '}
        {geofences.length} zone{geofences.length !== 1 ? 's' : ''} loaded
      </div>

      {/* The server refused this device's session: nothing is sent (positions queue locally) */}
      {sessionError && (
        <div
          role="alert"
          className="px-4 py-2 text-xs font-medium text-center bg-amber-500 text-white"
        >
          <FaExclamationTriangle className="inline mr-1" size={10} />
          {sessionError}
        </div>
      )}

      {/* Geofence loading error */}
      {geofenceError && (
        <div className="px-4 py-2 text-xs font-medium text-center bg-amber-500 text-white">
          <FaExclamationTriangle className="inline mr-1" size={10} />
          {geofenceError}
        </div>
      )}

      {/* Header */}
      <div className="bg-surface border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center">
            <FaTruck className="text-teal-600" size={18} />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">{regNo}</p>
            <p className="text-xs text-gray-400">{positionCount} updates sent</p>
          </div>
        </div>
        <button onClick={handleReset} className="text-xs text-gray-400 hover:text-gray-600">
          Change Loader
        </button>
      </div>

      {/* Tracking toggle */}
      <div className="p-4">
        <button
          onClick={() => setTracking(!tracking)}
          className={`w-full py-4 rounded-2xl font-bold text-lg transition-all ${
            tracking
              ? 'bg-red-500 text-white shadow-lg shadow-red-200'
              : 'bg-teal-600 text-white shadow-lg shadow-teal-200'
          }`}
        >
          <FaMapMarkerAlt className="inline mr-2" />
          {tracking ? 'Stop Tracking' : 'Start Tracking'}
        </button>
      </div>

      {/* Dashboard */}
      <DriverDashboard
        position={currentPosition}
        tracking={tracking}
        connected={connected}
        currentZone={currentZone}
        zoneEntryTime={zoneEntryTime}
        shiftLog={shiftLog}
      />

      {/* Location Tracker (invisible, handles GPS) */}
      <LocationTracker active={tracking} onLocationUpdate={handleLocationUpdate} />

      {/* Offline Sync Manager */}
      <OfflineSync ref={offlineSyncRef} onQueueChange={setOfflineCount} />
    </div>
  );
}
